import type { ReactNode } from 'react';

import { Icon, ICONS } from './icon';
import { NavLink } from './nav-link';

import css from './section.module.css';

type Props = {
  id: string;
  title: string;
  eyebrow?: string;
  /** "View all" style link at the right of the heading. */
  more?: { href: string; label: string };
  /** Phones show `more` under the content (the Mobile board) instead of at the right of the heading. */
  moreBelowOnMobile?: boolean;
  /** The page's own <h1> rather than a section <h2>. */
  level?: 1 | 2;
  className?: string;
  children: ReactNode;
};

// A content section with the design's heading block: serif title, tracked
// uppercase eyebrow, optional "View all" link.
export function Section({ id, title, eyebrow, more, moreBelowOnMobile, level = 2, className, children }: Props) {
  const Heading = level === 1 ? 'h1' : 'h2';
  const moreLink = (where: string) =>
    more ? (
      <NavLink href={more.href} className={`${css.more} ${where}`}>
        {more.label} <Icon d={ICONS.arrowLong} size={18} />
      </NavLink>
    ) : null;
  return (
    <section aria-labelledby={id} className={`${css.section} ${className ?? ''}`}>
      <div className={css.head}>
        <div>
          <Heading id={id} className={css.title}>
            {title}
          </Heading>
          {eyebrow ? <p className={css.eyebrow}>{eyebrow}</p> : null}
        </div>
        {moreLink(moreBelowOnMobile ? css.desktopOnly : '')}
      </div>
      {children}
      {moreBelowOnMobile ? moreLink(css.mobileOnly) : null}
    </section>
  );
}

/** Responsive card grid: one column on phones, up to four on desktop. */
export function CardGrid({ children }: { children: ReactNode }) {
  return <div className={css.grid}>{children}</div>;
}
